import React, { useState } from "react";
import { DL_FILTER_EVENT, DL_FILTER_ACTION_ON } from "helpers/constants";
import styled, { css } from "styled-components";
import ArrowDown from "svg/arrow-down.svg";
import { datalayerWrapper } from "lib/analytics/datalayerHelpers";

const Container = styled.div`
    position: relative;
    border: 1px solid ${({ theme }) => theme.colors.tertiary[600]};
    border-radius: 6px;
    box-sizing: border-box;
    height: 40px;

    &.left,
    &.right {
        width: 50%;
        display: inline-block;
    }

    &.left {
        float: left;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    &.right {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border-left-color: transparent;
    }

    ${({ hasFocus, theme }) =>
        hasFocus
            ? css`
                  border: 1px solid ${theme.colors.secondary[700]};
                  z-index: 1000;

                  &.right {
                      border-left-color: ${theme.colors.secondary[700]};
                  }
              `
            : ""}
`;

const StyledSelect = styled.select`
    width: 100%;
    height: 100%;
    font-family: ${({ theme }) => theme.typography.fontFamily.primary};
    padding: 9px 25px 7px 10px;
    font-size: 15px;
    border: none;
    border-radius: 6px;
    color: ${({ theme }) => theme.colors.tertiary[700]};
    appearance: none;
    background: white;
    outline: none;
    box-sizing: border-box;

    &:focus {
        outline: none;
    }

    /* CAUTION: IE hackery ahead */
    &::-ms-expand {
        display: none; /* remove default arrow in IE 10 and 11 */
    }

    ${({ hasSelection }) =>
        hasSelection
            ? css`
                  padding: 13px 25px 5px 10px;
              `
            : ""}
`;

const StyledArrowDown = styled(ArrowDown)`
    position: absolute;
    right: 4px;
    width: 24px;
    top: 50%;
    transform: translateY(-50%);
    fill: ${({ theme }) => theme.colors.tertiary[700]};
    pointer-events: none;
`;

const HelpText = styled.span`
    position: absolute;
    left: 10px;
    top: 4px;
    color: ${({ theme }) => theme.colors.tertiary[800]};
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

export const FilterSimpleSelect = ({
    options,
    value,
    filterId,
    classes,
    handleChange,
    datalayerKey,
    helpText = "",
    dataTestId = "filter-simple-select"
}) => {
    const [hasFocus, setHasFocus] = useState(false);

    if (!options) {
        return null;
    }

    const onChange = (event) => {
        const selection = event.target.value;
        if (selection) {
            datalayerWrapper({
                event: DL_FILTER_EVENT,
                filter_action: DL_FILTER_ACTION_ON,
                filter_name: datalayerKey,
                filter_value: selection.toLowerCase()
            });
        }
        handleChange(filterId, selection ? [selection] : []);
    };

    const currentValue = (Array.isArray(value) ? value[0] : value) || "";
    return (
        <Container
            hasSelection={!!currentValue}
            hasFocus={hasFocus}
            className={classes}
            data-testid={dataTestId}
        >
            {currentValue && <HelpText>{`${helpText}`}</HelpText>}
            <StyledArrowDown />
            <StyledSelect
                value={currentValue}
                onChange={onChange}
                onFocus={() => setHasFocus(true)}
                onBlur={() => setHasFocus(false)}
                hasSelection={!!currentValue}
                data-testid={`select-${filterId}`}
            >
                <option value="">{helpText}</option>
                {options.map(({ id, displayName }) => (
                    <option key={`${filterId}_${id}`} value={id}>
                        {displayName}
                    </option>
                ))}
            </StyledSelect>
        </Container>
    );
};
